import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Target, Eye, Award, ArrowRight, Sun, Flame, Wind, Activity } from "lucide-react";
import { PageHeader, SectionHeading, Reveal } from "@/components/public/ui";
import { useSite } from "@/context/SiteContext";

const RIDER = "https://images.unsplash.com/photo-1590506995460-d0d9892b54da?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const values = [
  { icon: ShieldCheck, title: "Safety First", body: "Hospital-grade sanitization that protects riders from germs, bacteria and viruses." },
  { icon: Award, title: "Verified Hygiene", body: "AI + RTM logs every cycle, giving businesses auditable proof of cleanliness." },
  { icon: Target, title: "Made for India", body: "Engineered locally for Indian conditions, spaces and usage patterns." },
];

const tech = [
  { icon: Sun, t: "UV-C Sterilization" },
  { icon: Flame, t: "Heat Treatment" },
  { icon: Wind, t: "HEPA Air Cleaning" },
  { icon: Activity, t: "AI + RTM Monitoring" },
];

export default function About() {
  const { settings } = useSite();
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow="About Us" title="Reimagining helmet hygiene for a healthier India" subtitle="Aimway Technology Solutions builds India's first AI + RTM technology-based helmet sanitization machines." />

      <section className="container-page py-24">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <Reveal><div className="overflow-hidden rounded-3xl shadow-card"><img src={RIDER} alt="Rider" className="h-full w-full object-cover" /></div></Reveal>
          <Reveal delay={0.1}>
            <SectionHeading eyebrow="Our Story" title="Clean helmets, powered by intelligent technology" subtitle={settings.about_short} />
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Shared and repeatedly-worn helmets are a hidden source of bacteria, sweat and odour. Aimway solves this with a
              contactless, chemical-free station that combines UV-C light, controlled heat and HEPA airflow — all validated in
              real time by AI. The result: 99.9% germ-free helmets in about 30 seconds, with verifiable hygiene data.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-24">
        <div className="container-page">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl border border-slate-100 bg-white p-8 shadow-card">
                <div className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-brand-sky text-brand"><Target size={22} /></div>
                <h3 className="font-display text-xl font-700 text-brand-navy">Our Mission</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">To make hygienic, sanitized helmets accessible everywhere — turning a daily health risk into a simple, 30-second habit.</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-2xl border border-slate-100 bg-white p-8 shadow-card">
                <div className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-brand-orange-light text-brand-orange"><Eye size={22} /></div>
                <h3 className="font-display text-xl font-700 text-brand-navy">Our Vision</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">An Aimway station at every petrol pump, office, mall and campus in India — setting a new national standard for shared-gear hygiene.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="container-page py-24">
        <SectionHeading center eyebrow="Why Aimway" title="Values that drive us" className="mb-14" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-card">
                <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-full bg-brand-sky text-brand"><v.icon size={24} /></div>
                <h3 className="font-display text-lg font-700 text-brand-navy">{v.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-navy py-20 text-white">
        <div className="container-page">
          <SectionHeading center eyebrow="The Technology" title="Four systems. One clean helmet." className="mb-12 [&_h2]:text-white" />
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {tech.map((x, i) => (
              <Reveal key={x.t} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
                  <x.icon size={26} className="mx-auto text-brand-orange" />
                  <div className="mt-4 font-display text-sm font-700">{x.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 font-display font-700 text-white transition-colors hover:bg-brand-orange-dark">Talk to our team <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
